import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, LogOut, Lock, Settings, UsersRound } from "lucide-react";

import { cn, getFromLocalStorage, setToLocalStorage } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";
import paylinqLogoWhite from "src/images/paylinq-logo-white.png";
import { checkPermission } from "src/hooks/useHasPermissions";
import type { AuthUser, PermissionCheck } from "src/types/auth";
import { buildNavGroups, type NavChild, type NavItem } from "./navConfig";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";

const COLLAPSED_KEY = "collapsed";

export const useSidebarCollapsed = () => {
	const [collapsed, setCollapsed] = useState<boolean>(() => Boolean(getFromLocalStorage(COLLAPSED_KEY, false)));

	useEffect(() => {
		setToLocalStorage(COLLAPSED_KEY, collapsed);
	}, [collapsed]);

	return { collapsed, setCollapsed };
};

const initialsOf = (user: AuthUser | null) =>
	`${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase() || "PL";

const isPathActive = (pathname: string, path?: string) => {
	if (!path) return false;
	if (path === "/dashboard") return pathname === path;
	return pathname === path || pathname.startsWith(`${path}/`);
};

interface SidebarContentProps {
	collapsed: boolean;
	onToggleCollapse?: () => void;
	/** Called after any navigation — used to close the mobile drawer. */
	onNavigate?: () => void;
}

const SubNavLink = ({
	child,
	permissions,
	onNavigate,
}: {
	child: NavChild;
	permissions: string[];
	onNavigate?: () => void;
}) => {
	const { pathname } = useLocation();

	if (!checkPermission(child.permission as PermissionCheck, permissions)) return null;

	const active = pathname === child.path;

	return (
		<Link
			to={child.path}
			onClick={onNavigate}
			className={cn(
				"tw-block tw-rounded-lg tw-py-2 tw-pl-[42px] tw-pr-3 tw-text-[13px] tw-transition-colors",
				active ? "tw-bg-teal tw-text-white tw-font-medium" : "tw-text-[#A9B3C9] hover:tw-bg-navy-hover hover:tw-text-white"
			)}
		>
			{child.name}
		</Link>
	);
};

const NavLink = ({
	item,
	collapsed,
	permissions,
	onNavigate,
}: {
	item: NavItem;
	collapsed: boolean;
	permissions: string[];
	onNavigate?: () => void;
}) => {
	const { pathname } = useLocation();
	const Icon = item.icon;

	const visibleChildren = (item.children ?? []).filter((child) =>
		checkPermission(child.permission as PermissionCheck, permissions)
	);
	const hasChildren = visibleChildren.length > 0;
	const childActive = visibleChildren.some((child) => pathname === child.path);
	const active = isPathActive(pathname, item.path) || childActive;

	// Sub-lists start open when one of their routes is active, and collapse away entirely
	// when the sidebar is icon-only.
	const [open, setOpen] = useState(active);
	useEffect(() => {
		if (active) setOpen(true);
	}, [active]);

	const rowClass = cn(
		"tw-flex tw-w-full tw-items-center tw-gap-[11px] tw-rounded-[10px] tw-px-2.5 tw-py-2.5 tw-text-[14px] tw-font-medium tw-transition-colors",
		collapsed && "tw-justify-center tw-px-2",
		item.locked && "tw-cursor-not-allowed tw-opacity-70",
		active && !item.locked ? "tw-bg-teal tw-text-white" : "tw-text-[#C7CFE2] hover:tw-bg-navy-hover hover:tw-text-white"
	);

	if (item.locked) {
		return (
			<div className={rowClass} title={`${item.name} — coming soon`} aria-disabled="true">
				<Icon className="tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-opacity-85" />
				{!collapsed && (
					<>
						<span className="tw-flex-1 tw-text-left">{item.name}</span>
						<Lock className="tw-h-[13px] tw-w-[13px] tw-shrink-0 tw-opacity-55" />
					</>
				)}
			</div>
		);
	}

	if (hasChildren && !collapsed) {
		return (
			<div>
				<button type="button" className={rowClass} onClick={() => setOpen((prev) => !prev)} aria-expanded={open}>
					<Icon className="tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-opacity-85" />
					<span className="tw-flex-1 tw-text-left">{item.name}</span>
					<ChevronDown className={cn("tw-h-4 tw-w-4 tw-shrink-0 tw-transition-transform", open && "tw-rotate-180")} />
				</button>
				{open && (
					<div className="tw-mt-0.5 tw-space-y-0.5">
						{visibleChildren.map((child) => (
							<SubNavLink key={child.path} child={child} permissions={permissions} onNavigate={onNavigate} />
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<Link to={item.path ?? "#"} onClick={onNavigate} className={rowClass} title={collapsed ? item.name : undefined}>
			<Icon className="tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-opacity-85" />
			{!collapsed && <span className="tw-flex-1 tw-text-left">{item.name}</span>}
		</Link>
	);
};

export const SidebarContent = ({ collapsed, onToggleCollapse, onNavigate }: SidebarContentProps) => {
	const { user, logout } = useAuth();
	const permissions = user?.permissions ?? [];
	const groups = buildNavGroups(user);

	const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Your account";

	return (
		<div className="tw-flex tw-h-full tw-flex-col tw-bg-navy tw-px-3 tw-py-5 tw-text-white">
			{/* Brand */}
			<div className={cn("tw-flex tw-items-center tw-gap-2.5 tw-px-2 tw-pb-5 tw-pt-1.5", collapsed && "tw-justify-center tw-px-0")}>
				{!collapsed && <img src={paylinqLogoWhite} alt="Paylinq" className="tw-h-7 tw-w-auto" />}
				{onToggleCollapse && (
					<button
						type="button"
						onClick={onToggleCollapse}
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						className={cn(
							"tw-flex tw-h-[26px] tw-w-[26px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[7px] tw-text-white/60 tw-transition-colors hover:tw-bg-white/10 hover:tw-text-white",
							!collapsed && "tw-ml-auto"
						)}
					>
						<ChevronLeft className={cn("tw-h-[15px] tw-w-[15px] tw-transition-transform", collapsed && "tw-rotate-180")} />
					</button>
				)}
			</div>

			{/* Nav */}
			<nav className="tw-flex-1 tw-overflow-y-auto">
				{groups.map((group, groupIndex) => {
					const items = group.items.filter((item) => checkPermission(item.permission as PermissionCheck, permissions));
					if (items.length === 0) return null;

					return (
						<div key={group.label ?? `group-${groupIndex}`}>
							{group.label && !collapsed && (
								<div className="tw-px-2.5 tw-pb-1.5 tw-pt-4 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-[#7C88A6]">
									{group.label}
								</div>
							)}
							{group.label && collapsed && <div className="tw-my-2 tw-h-px tw-bg-white/10" />}
							<div className="tw-space-y-0.5">
								{items.map((item) => (
									<NavLink
										key={item.path ?? item.name}
										item={item}
										collapsed={collapsed}
										permissions={permissions}
										onNavigate={onNavigate}
									/>
								))}
							</div>
						</div>
					);
				})}
			</nav>

			{/* User menu */}
			<div className="tw-mt-auto tw-border-t tw-border-white/10 tw-pt-2">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className={cn(
								"tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-[10px] tw-p-2 tw-transition-colors hover:tw-bg-white/[0.06]",
								collapsed && "tw-justify-center tw-px-0"
							)}
						>
							<div className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-teal tw-text-[12px] tw-font-bold">
								{initialsOf(user)}
							</div>
							{!collapsed && (
								<>
									<div className="tw-min-w-0 tw-flex-1 tw-text-left">
										<div className="tw-truncate tw-text-[13.5px] tw-font-bold">{fullName}</div>
										<div className="tw-text-[11.5px] tw-text-white/55">{user?.business_name ?? "Account"}</div>
									</div>
									<ChevronDown className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-white/50" />
								</>
							)}
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start" side="top" className="tw-w-[240px]">
						<div className="tw-flex tw-items-center tw-gap-2.5 tw-px-2 tw-py-2">
							<div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-teal-tint tw-text-[13px] tw-font-bold tw-text-teal-dark">
								{initialsOf(user)}
							</div>
							<div className="tw-min-w-0">
								<div className="tw-truncate tw-text-[13px] tw-font-semibold">{fullName}</div>
								<div className="tw-truncate tw-text-[12px] tw-text-text-soft">{user?.email}</div>
							</div>
						</div>
						<DropdownMenuSeparator />
						<DropdownMenuItem asChild>
							<Link to="/settings" onClick={onNavigate}>
								<Settings />
								Account settings
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link to="/team" onClick={onNavigate}>
								<UsersRound />
								Team management
							</Link>
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem variant="danger" onSelect={() => logout()}>
							<LogOut />
							Log out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
};

/** Desktop sidebar. Hidden below `lg`, where the drawer in ProtectedRoute takes over. */
const Sidebar = ({ collapsed, onToggleCollapse }: { collapsed: boolean; onToggleCollapse: () => void }) => (
	<aside
		className={cn(
			"tw-sticky tw-top-0 tw-hidden tw-h-screen tw-shrink-0 tw-transition-[width] tw-duration-200 lg:tw-block",
			collapsed ? "tw-w-[74px]" : "tw-w-[246px]"
		)}
	>
		<SidebarContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
	</aside>
);

export default Sidebar;
